import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { useCallback } from "react";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import type { Language, ReactFramework } from "@/utils/templateProjects";
import { getUnexpiredToken } from "@/utils/utils";

export const useSetupProject = () => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const { session } = useSession();
  const setupProject = useCallback(
    async (
      canvasId: string,
      options: {
        guessFramework?: boolean;
        guessPackageManager?: boolean;
        guessLanguage?: boolean;
      },
    ) => {
      if (!supabaseClient || !session) {
        return;
      }
      if (
        options.guessFramework ||
        options.guessPackageManager ||
        options.guessLanguage
      ) {
        // get the guessed project params from the vercel ai configureProject workflow
        const response = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/ai/vercel-ai/configureProject`,
          {},
          {
            headers: {
              Authorization: `Bearer ${await getUnexpiredToken(session)}`,
            },
          },
        );
        const params = response.data as {
          framework: ReactFramework;
          language: Language;
          packageManager: string;
        };
        // update the project settings with the guessed params
        await supabaseClient
          .from("projects")
          .update({
            framework: options.guessFramework ? params.framework : undefined,
            language: options.guessLanguage ? params.language : undefined,
            packageManager: options.guessPackageManager
              ? params.packageManager
              : undefined,
          })
          .eq("id", canvasId);
      }
      useAiContextStore.getState().setSendChatMessage({
        sendChatMessage:
          "Perform all the steps to set this project up for Tempo. Including (if needed): adding to the gitignore",
        isFreePrompt: true,
      });
      useLayoutStore.getState().setNewLayout(LeftTab.AIChat, undefined);

      track("AI_PROMPT_REQUESTED", {
        type: "INSTALL_WITH_AI",
        isFreePrompt: true,
      });
    },
    [session, supabaseClient],
  );
  return { setupProject };
};
